"use client";

import { create } from 'zustand';
import { apiService } from '../services/api/apiClient';
import { isAuthenticated } from '../lib/auth';
import { useWishlistStore } from './useWishlistStore';

const CART_STORAGE_KEY = "usave_cart";
const CART_STORAGE_META_KEY = "usave_cart_meta";

// Helper function to safely parse number

// Helper function to safely parse number
const safeParseNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Calculate totals from cart items
const calculateTotals = (items, shippingType = 'delivery', deliveryConfig = null) => {
  if (!items || !Array.isArray(items)) {
    return { subtotal: 0, tax: 0, shipping: 0, total: 0, itemCount: 0 };
  }

  const subtotal = items.reduce((sum, item) => {
    if (!item) return sum;

    // Try to get price from multiple possible locations
    const price = safeParseNumber(
      item.product?.discountedPrice ||
      item.product?.originalPrice ||
      item.product?.price ||
      item.discountedPrice ||
      item.originalPrice ||
      item.price
    );

    // Ensure quantity is a valid number
    const quantity = safeParseNumber(item.quantity, 1);

    const itemTotal = price * quantity;
    return sum + (isNaN(itemTotal) ? 0 : itemTotal);
  }, 0);

  const tax = 0;
  
  // Use deliveryConfig for pricing, with fallbacks
  const standardFee = safeParseNumber(deliveryConfig?.standardFee, 18);
  const freeThreshold = safeParseNumber(deliveryConfig?.freeDeliveryThreshold, 0);

  // Use current shippingType to decide cost
  let shipping = standardFee;
  if (shippingType === 'pickup' || items.length === 0) {
    shipping = 0;
  } else if (freeThreshold > 0 && subtotal >= freeThreshold) {
    shipping = 0;
  }
  
  const total = safeParseNumber(subtotal + tax + shipping);
  const itemCount = items.reduce((sum, item) => {
    const quantity = safeParseNumber(item?.quantity, 0);
    return sum + quantity;
  }, 0);

  return {
    subtotal: isNaN(subtotal) ? 0 : subtotal,
    tax: isNaN(tax) ? 0 : tax,
    shipping: isNaN(shipping) ? 0 : shipping,
    total: isNaN(total) ? 0 : total,
    itemCount: isNaN(itemCount) ? 0 : itemCount
  };
};

// Normalize cart item to ensure all numeric fields are valid numbers
const normalizeCartItem = (item) => {
  if (!item) return null;

  const normalizedOptions = item.options && typeof item.options === 'object' ? item.options : {};
  const normalizedColor = item.color ?? normalizedOptions.color ?? null;
  const normalizedSize = item.size ?? normalizedOptions.size ?? null;
  const normalizedInstallation = item.includeInstallation ?? normalizedOptions.includeInstallation ?? false;

  return {
    ...item,
    quantity: safeParseNumber(item.quantity, 1),
    ...(normalizedColor ? { color: normalizedColor } : {}),
    ...(normalizedSize ? { size: normalizedSize } : {}),
    includeInstallation: Boolean(normalizedInstallation),
    options: {
      ...normalizedOptions,
      ...(normalizedColor ? { color: normalizedColor } : {}),
      ...(normalizedSize ? { size: normalizedSize } : {}),
      includeInstallation: Boolean(normalizedInstallation),
    },
    price: safeParseNumber(item.price || item.product?.price),
    discountedPrice: safeParseNumber(item.discountedPrice || item.product?.discountedPrice),
    originalPrice: safeParseNumber(item.originalPrice || item.product?.originalPrice),
    product: item.product ? {
      ...item.product,
      originalPrice: safeParseNumber(item.product.originalPrice || item.product.price || item.product.regularPrice),
      discountedPrice: safeParseNumber(item.product.discountedPrice || item.product.salePrice || item.product.originalPrice || item.product.price),
      price: safeParseNumber(item.product.discountedPrice || item.product.salePrice || item.product.originalPrice || item.product.price || item.product.regularPrice),
      regularPrice: safeParseNumber(item.product.regularPrice || item.product.originalPrice || item.product.price),
      salePrice: safeParseNumber(item.product.salePrice || item.product.discountedPrice),
      stockQuantity: safeParseNumber(item.product.stockQuantity ?? item.product.stock ?? 0),
      stock: safeParseNumber(item.product.stock ?? item.product.stockQuantity ?? 0),
      rating: safeParseNumber(item.product.rating),
      reviews: safeParseNumber(item.product.reviews),
    } : undefined,
  };
};

const buildCartVariantKey = (item = {}) => {
  const options = item.options && typeof item.options === 'object' ? item.options : {};
  const color = item.color ?? options.color ?? null;
  const size = item.size ?? options.size ?? null;
  const includeInstallation = item.includeInstallation ?? options.includeInstallation ?? false;

  return JSON.stringify({
    color: color ? String(color) : null,
    size: size ? String(size) : null,
    includeInstallation: Boolean(includeInstallation),
  });
};

const isSyntheticCartId = (value) => typeof value === 'string' && value.startsWith('cart_');

const buildCartItemRequestPayload = (item = {}) => {
  const normalizedItem = normalizeCartItem(item);
  const payload = {};

  if (normalizedItem?.id && !isSyntheticCartId(String(normalizedItem.id))) {
    payload.id = String(normalizedItem.id);
    return payload;
  }

  const productId = normalizedItem?.productId || normalizedItem?.product?.id || normalizedItem?.id;
  if (productId) {
    payload.productId = String(productId);
  }

  const options = normalizedItem?.options && typeof normalizedItem.options === 'object'
    ? { ...normalizedItem.options }
    : {};

  const color = normalizedItem?.color ?? options.color ?? null;
  const size = normalizedItem?.size ?? options.size ?? null;
  const includeInstallation = normalizedItem?.includeInstallation ?? options.includeInstallation ?? false;

  if (color) payload.color = color;
  if (size) payload.size = size;
  payload.includeInstallation = Boolean(includeInstallation);

  if (Object.keys(options).length > 0 || color || size || includeInstallation !== undefined) {
    payload.options = {
      ...options,
      ...(color ? { color } : {}),
      ...(size ? { size } : {}),
      includeInstallation: Boolean(includeInstallation),
    };
  }

  return payload;
};

const findCartItemMatch = (items, identifier) => {
  const target = String(identifier);

  const byId = items.find(item => String(item?.id) === target);
  if (byId) return byId;

  const byProductId = items.filter(item => String(item?.productId) === target || String(item?.product?.id) === target);
  if (byProductId.length === 1) return byProductId[0];

  return null;
};

const areCartCollectionsEquivalent = (leftItems = [], rightItems = []) => {
  if (leftItems.length !== rightItems.length) return false;

  const normalizeForCompare = (item) => {
    const normalized = normalizeCartItem(item);
    return JSON.stringify({
      productId: String(normalized?.productId || normalized?.product?.id || normalized?.id || ''),
      variantKey: buildCartVariantKey(normalized),
      quantity: safeParseNumber(normalized?.quantity, 0),
    });
  };

  const left = leftItems.map(normalizeForCompare).sort();
  const right = rightItems.map(normalizeForCompare).sort();

  return left.every((entry, index) => entry === right[index]);
};

const loadCartSnapshotFromStorage = () => {
  if (typeof window === 'undefined') {
    return { items: [], meta: { source: 'guest', userId: null }, hasMeta: false };
  }

  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const metaStored = localStorage.getItem(CART_STORAGE_META_KEY);
    if (!stored) return { items: [], meta: { source: 'guest', userId: null }, hasMeta: false };

    const items = JSON.parse(stored);
    const meta = metaStored ? JSON.parse(metaStored) : { source: 'guest', userId: null };

    return {
      items: Array.isArray(items) ? items.map(normalizeCartItem).filter(Boolean) : [],
      meta: {
        source: meta?.source === 'account' ? 'account' : 'guest',
        userId: meta?.userId ? String(meta.userId) : null,
      },
      hasMeta: Boolean(metaStored),
    };
  } catch (err) {
    console.error('Error loading cart from localStorage:', err);
    return { items: [], meta: { source: 'guest', userId: null }, hasMeta: false };
  }
};

const loadCartFromStorage = () => loadCartSnapshotFromStorage().items;

const saveCartToStorage = (items, meta = {}) => {
  if (typeof window === 'undefined') return false;

  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    localStorage.setItem(CART_STORAGE_META_KEY, JSON.stringify({
      source: meta.source === 'account' ? 'account' : 'guest',
      userId: meta.userId ? String(meta.userId) : null,
    }));
    return true;
  } catch (err) {
    console.error('Error saving cart to localStorage:', err);
    return false;
  }
};

const clearLegacyCartStorage = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_STORAGE_META_KEY);
  } catch (err) {
    console.error('Error clearing cart from localStorage:', err);
  }
};

const persistGuestCartStorage = (items) => saveCartToStorage(items, { source: 'guest' });

const syncStorageForCurrentAuthState = (items) => {
  if (isAuthenticated()) {
    clearLegacyCartStorage();
    return true;
  }

  return persistGuestCartStorage(items);
};

const extractGuestCartItems = (cartItems = []) =>
  cartItems
    .map(normalizeCartItem)
    .filter((item) => item && isSyntheticCartId(String(item.id || '')));

// Save cart to database as single object (for authenticated users)
const saveCartToDatabase = async (items) => {
  if (!isAuthenticated()) return { success: false };

  try {
    // Format items for API - ensure productId and quantity are present
    const formattedItems = items.map(item => {
      // Extract productId from various possible fields
      const productId = item.productId || item.id || item.product?.id;
      const quantity = item.quantity || 1;

      if (!productId) {
        throw new Error(`Missing product ID for cart item: ${JSON.stringify(item)}`);
      }

      return {
        productId: productId,
        quantity: quantity,
        color: item.color || item.options?.color || null,
        size: item.size || item.options?.size || null,
        includeInstallation: item.includeInstallation ?? item.options?.includeInstallation ?? false,
        options: item.options || undefined,
      };
    });

    // Save entire cart as a single object
    const cartData = {
      items: formattedItems,
    };

    // Use cart save endpoint (saves entire cart as single object)
    const response = await apiService.cart.save(cartData);

    if (response?.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response?.error || 'Failed to save cart' };
  } catch (err) {
    console.log('Failed to save cart to database:', err);
    return { success: false, error: err.message };
  }
};

// Create cart store with localStorage guest persistence and DB persistence for authenticated users
const storeCreator = (set, get) => {
  const initialItems = typeof window !== 'undefined' ? loadCartFromStorage() : [];
  const initialTotals = calculateTotals(initialItems, 'delivery');

  return {
    cartItems: initialItems,
    totals: initialTotals,
    shippingType: 'delivery',
    deliveryConfig: null,
    isLoading: false,
    isSyncing: false,
    deletingItemIds: {},
    error: null,
    hasLoadedCart: false,
    isInitializing: false,

    // Internal helper to update cart state and totals together
    _updateCart: (items) => {
      const { shippingType, deliveryConfig } = get();
      const newTotals = calculateTotals(items, shippingType, deliveryConfig);
      set({
        cartItems: items,
        totals: newTotals
      });
      return { items, totals: newTotals };
    },

    setShippingType: (type) => {
      const { cartItems, deliveryConfig } = get();
      const newTotals = calculateTotals(cartItems, type, deliveryConfig);
      set({
        shippingType: type,
        totals: newTotals
      });
      return { items: cartItems, totals: newTotals };
    },

    // Load settings from backend
    loadSettings: async () => {
      try {
        const response = await apiService.settings.getPublic();
        if (response?.success && response.data?.delivery) {
          const { cartItems, shippingType } = get();
          const deliveryConfig = response.data.delivery;
          const newTotals = calculateTotals(cartItems, shippingType, deliveryConfig);
          
          set({ 
            deliveryConfig,
            totals: newTotals
          });
        }
      } catch (err) {
        console.warn('Failed to load delivery settings:', err);
      }
    },

    // Load cart from localStorage for guests and from DB for authenticated users.
    loadCart: async (forceReload = false) => {
      const { isInitializing, hasLoadedCart } = get();

      // Prevent multiple simultaneous loads
      if (isInitializing && !forceReload) {
        return { items: get().cartItems, totals: get().totals };
      }

      // Refresh delivery settings even when the cart is already initialized,
      // so admin fee changes are picked up without requiring a full hard reset.
      if (hasLoadedCart && !forceReload) {
        await get().loadSettings();
        return { items: get().cartItems, totals: get().totals };
      }

      set({ isInitializing: true, isLoading: false, error: null });

      try {
        // Load delivery settings first
        await get().loadSettings();

        let loadedItems = isAuthenticated() ? get().cartItems : loadCartFromStorage();
        let loadedTotals = calculateTotals(loadedItems, get().shippingType, get().deliveryConfig);
        set({ cartItems: loadedItems, totals: loadedTotals });

        // For authenticated users, the database is the source of truth.
        if (isAuthenticated()) {
          try {
            set({ isLoading: true });
            const response = await apiService.cart.get();
            const apiItemsRaw = response?.data?.items ?? response?.data?.cart?.items;
            const userId = response?.data?.userId ?? response?.data?.cart?.userId ?? null;

            if (response?.success && Array.isArray(apiItemsRaw)) {
              loadedItems = apiItemsRaw.map(normalizeCartItem).filter(Boolean);
              loadedTotals = calculateTotals(loadedItems, get().shippingType, get().deliveryConfig);
              clearLegacyCartStorage();
            }
          } catch (err) {
            console.debug('[Cart] API sync failed, using local cart cache:', err.message);
          }
        } else {
          persistGuestCartStorage(loadedItems);
        }

        if (isAuthenticated() && loadedItems.length === 0) {
          try {
            clearLegacyCartStorage();
          } catch (err) {
            console.debug('[Cart] Failed to refresh local cart cache:', err.message);
          }
        }

      set({
        cartItems: loadedItems,
        totals: loadedTotals,
        isLoading: false,
        isInitializing: false,
        deletingItemIds: {},
        hasLoadedCart: true
      });
        return { items: loadedItems, totals: loadedTotals };
      } catch (err) {
        console.error('Error loading cart:', err);
        set({
          error: 'Failed to load cart',
          isLoading: false,
          isInitializing: false
        });
        return { items: [], totals: { subtotal: 0, tax: 0, shipping: 0, total: 0, itemCount: 0 } };
      }
    },

    resetLocalCart: () => {
      clearLegacyCartStorage();
      set({
        cartItems: [],
        totals: calculateTotals([], get().shippingType, get().deliveryConfig),
        isLoading: false,
        isSyncing: false,
        deletingItemIds: {},
        error: null,
        hasLoadedCart: false,
        isInitializing: false
      });
      return { success: true };
    },

    // Sync local cart items to the authenticated user's account (merge instead of replace)
    syncLocalToServer: async (guestCartSnapshot = null) => {
      if (!isAuthenticated()) {
        return { success: false, error: 'Not authenticated' };
      }

      const { cartItems } = get();
      const storedSnapshot = loadCartSnapshotFromStorage();
      const guestItemsFromStorage = storedSnapshot.meta?.source === 'guest'
        ? storedSnapshot.items
        : [];
      const guestItemsFromState = extractGuestCartItems(cartItems);
      const guestItems = Array.isArray(guestCartSnapshot) && guestCartSnapshot.length > 0
        ? guestCartSnapshot.map(normalizeCartItem).filter(Boolean)
        : guestItemsFromStorage.length > 0
          ? guestItemsFromStorage
          : guestItemsFromState;
      
      set({ isSyncing: true, isLoading: true });

      try {
        // 1. Fetch current server cart
        const response = await apiService.cart.get();
        let serverItems = [];
        
        if (response.success && response.data?.items) {
          serverItems = response.data.items.map(normalizeCartItem).filter(Boolean);
        }

        const shouldSkipGuestMerge = guestItems.length > 0 &&
          !storedSnapshot.hasMeta &&
          areCartCollectionsEquivalent(guestItems, serverItems);

        // 2. Merge local items into server items
        // If the same product exists in both, we can either take the local or sum them.
        // Usually, for login sync, we merge by ensuring all local items are present.
        const mergedItems = [...serverItems];
        
        if (guestItems.length > 0 && !shouldSkipGuestMerge) {
          guestItems.forEach(localItem => {
            const localPid = String(localItem.productId || localItem.product?.id || localItem.id);
            const localVariantKey = buildCartVariantKey(localItem);
            const serverItemIndex = mergedItems.findIndex(si => {
              const serverPid = String(si.productId || si.product?.id || si.id);
              const serverVariantKey = buildCartVariantKey(si);
              return serverPid === localPid && serverVariantKey === serverVariantKey;
            });
            
            if (serverItemIndex >= 0) {
              // Option 1: Combine quantities (more intuitive for guest-to-user sync)
              mergedItems[serverItemIndex].quantity += localItem.quantity;
            } else {
              // Option 2: Add as new item
              mergedItems.push(normalizeCartItem(localItem));
            }
          });
        }

        // 3. Save merged cart back to server
        if (mergedItems.length > 0) {
          await saveCartToDatabase(mergedItems);
        } else {
          await saveCartToDatabase([]);
        }

        // 4. Guest local cart has been transferred into the account cart.
        clearLegacyCartStorage();

        // 5. Reload the cart from the database so the account state is authoritative.
        const refreshedResponse = await apiService.cart.get();
        const refreshedItemsRaw = refreshedResponse?.data?.items ?? refreshedResponse?.data?.cart?.items ?? [];
        const refreshedItems = Array.isArray(refreshedItemsRaw)
          ? refreshedItemsRaw.map(normalizeCartItem).filter(Boolean)
          : [];
        const finalTotals = calculateTotals(refreshedItems, get().shippingType, get().deliveryConfig);

        clearLegacyCartStorage();
        set({
          cartItems: refreshedItems,
          totals: finalTotals,
          isSyncing: false,
          isLoading: false,
          hasLoadedCart: true
        });

        if (mergedItems.length > 0 && guestItems.length > 0) {
          // Toast removed to prevent double notification if handled elsewhere
          // showToast(`Synced ${cartItems.length} items to your account`, 'success');
        }

        return { success: true, items: mergedItems };
      } catch (err) {
        console.error('[Cart] Sync failed:', err);
        set({ isSyncing: false, isLoading: false, error: err.message });
        return { success: false, error: err.message };
      }
    },

    // Save cart to database (for authenticated users)
    saveCart: async () => {
      const { cartItems } = get();

      if (!isAuthenticated()) {
        syncStorageForCurrentAuthState(cartItems);
        return { success: true };
      }

      try {
        set({ isSyncing: true });
        const result = await saveCartToDatabase(cartItems);
        set({ isSyncing: false });
        return result;
      } catch (err) {
        set({ isSyncing: false });
        console.error('Error saving cart:', err);
        return { success: false, error: err.message };
      }
    },

    // Add to cart - works for both authenticated and unauthenticated users
    // Supports options (color, size) for product variants
    addToCart: async (productOrId, quantity = 1, options = {}) => {
      set({ isLoading: true, error: null });

      try {
        // Determine product ID and product data
        let productId;
        let productData;

        if (typeof productOrId === 'object' && productOrId !== null) {
          // Prioritize actual product ID over store item ID (which might be prefixed)
          productId = productOrId.productId || productOrId.product?.id || productOrId.id;
          // If it's a store item (has .product), use the inner product for data
          productData = productOrId.product || productOrId;
        } else {
          productId = productOrId;
          productData = null;
        }

        if (!productId) {
          throw new Error('Product ID is required');
        }

        // Normalize productId to string for consistent comparison
        const pid = String(productId);

        // Extract and normalize options (color, size, etc.)
        const { color, size, ...otherOptions } = options;
        const normalizedColor = color ? (typeof color === 'string' ? color : (color.value || color.name || JSON.stringify(color))) : null;
        const normalizedSize = size ? (typeof size === 'string' ? size : (size.value || size.label || JSON.stringify(size))) : null;

        // Check stock availability if product data is available
        if (productData) {
          const stockQuantity = productData.stockQuantity ?? productData.stock ?? 0;
          const inStock = productData.inStock !== false && productData.inStock !== null;
          const hasStock = inStock && (stockQuantity > 0 || stockQuantity === 0); // Allow 0 stock if inStock is true

          if (!inStock || (stockQuantity > 0 && inStock === false)) {
            set({ isLoading: false });
            return { success: false, error: 'This product is out of stock' };
          }

          // Check if adding quantity would exceed available stock (only if stock is tracked)
          const { cartItems } = get();
          const existingItem = cartItems.find(item => {
            const sameProduct = String(item.productId) === pid || String(item.id) === pid || String(item.product?.id) === pid;
            if (!sameProduct) return false;
            // Check if options match
            const itemColor = item.color || item.options?.color;
            const itemSize = item.size || item.options?.size;
            const normalizedItemColor = itemColor ? (typeof itemColor === 'string' ? itemColor : (itemColor.value || itemColor.name || itemColor)) : null;
            const normalizedItemSize = itemSize ? (typeof itemSize === 'string' ? itemSize : (itemSize.value || itemSize.label || itemSize)) : null;
            const sameColor = (!normalizedColor && !normalizedItemColor) || (normalizedColor === normalizedItemColor);
            const sameSize = (!normalizedSize && !normalizedItemSize) || (normalizedSize === normalizedItemSize);
            return sameColor && sameSize;
          });
          const currentQuantity = existingItem ? existingItem.quantity : 0;
          const newQuantity = currentQuantity + quantity;

          if (stockQuantity > 0 && newQuantity > stockQuantity) {
            set({ isLoading: false });
            return {
              success: false,
              error: `Only ${stockQuantity} item(s) available in stock`
            };
          }
        }

        // OPTIMISTIC UPDATE: Update local state immediately
        const { cartItems } = get();
        const existingItemIndex = cartItems.findIndex(item => {
          const sameProduct = String(item.productId) === pid || String(item.id) === pid || String(item.product?.id) === pid;
          if (!sameProduct) return false;
          // Check if options match
          const itemColor = item.color || item.options?.color;
          const itemSize = item.size || item.options?.size;
          const normalizedItemColor = itemColor ? (typeof itemColor === 'string' ? itemColor : (itemColor.value || itemColor.name || itemColor)) : null;
          const normalizedItemSize = itemSize ? (typeof itemSize === 'string' ? itemSize : (itemSize.value || itemSize.label || itemSize)) : null;
          const sameColor = (!normalizedColor && !normalizedItemColor) || (normalizedColor === normalizedItemColor);
          const sameSize = (!normalizedSize && !normalizedItemSize) || (normalizedSize === normalizedItemSize);
          return sameColor && sameSize;
        });

        let newItems;
        if (existingItemIndex >= 0) {
          // Update quantity of existing item with same options
          const existingItem = cartItems[existingItemIndex];
          const currentQuantity = safeParseNumber(existingItem.quantity, 1);
          const addQuantity = safeParseNumber(quantity, 1);
          const newQuantity = currentQuantity + addQuantity;

          const updatedItem = {
            ...existingItem,
            quantity: newQuantity,
            // Update options if provided
            ...(normalizedColor && { color: normalizedColor }),
            ...(normalizedSize && { size: normalizedSize }),
            options: {
              ...(existingItem.options || {}),
              ...(normalizedColor && { color: normalizedColor }),
              ...(normalizedSize && { size: normalizedSize }),
              ...otherOptions,
            },
          };

          newItems = cartItems.map((item, index) =>
            index === existingItemIndex ? updatedItem : item
          );
        } else {
          // Add new item to cart with options
          const uniqueId = `cart_${Date.now()}_${pid}_${Math.random().toString(36).substr(2, 9)}`;
          const newItem = {
            id: uniqueId,
            productId: pid,
            quantity: safeParseNumber(quantity, 1),
            // Store options at root level for easy access
            ...(normalizedColor && { color: normalizedColor }),
            ...(normalizedSize && { size: normalizedSize }),
            // Also store in options object for structured access
            options: {
              ...(normalizedColor && { color: normalizedColor }),
              ...(normalizedSize && { size: normalizedSize }),
              ...otherOptions,
            },
            ...(productData && {
              product: productData,
              title: productData.title || productData.name || '',
              image: productData.image || productData.images?.[0] || '',
              discountedPrice: safeParseNumber(productData.discountedPrice || productData.price),
              originalPrice: safeParseNumber(productData.originalPrice || productData.price),
              price: safeParseNumber(productData.discountedPrice || productData.originalPrice || productData.price),
            }),
          };
          newItems = [...cartItems, newItem];
        }

        const newTotals = calculateTotals(newItems, get().shippingType, get().deliveryConfig);

        // OPTIMISTIC UPDATE: Update state immediately
        set({
          cartItems: newItems,
          totals: newTotals,
          isLoading: false
        });

        syncStorageForCurrentAuthState(newItems);

        // Remove from wishlist if it exists there (mutual exclusivity)
        try {
          const wishlistStore = useWishlistStore.getState();
          const isInWishlist = wishlistStore.isInWishlist(pid);
          if (isInWishlist) {
            // Remove from wishlist (don't await - let it run in background)
            wishlistStore.removeFromWishlist(pid).catch((err) => {
              console.debug('[Cart] Failed to remove from wishlist:', err.message);
            });
          }
        } catch (err) {
          // Silently fail if wishlist store is not available
          console.debug('[Cart] Could not check/remove from wishlist:', err.message);
        }

        // If authenticated, persist the exact item before returning success.
        if (isAuthenticated()) {
          const requestPayload = {
            productId: pid,
            quantity: safeParseNumber(quantity, 1),
            ...(normalizedColor ? { color: normalizedColor } : {}),
            ...(normalizedSize ? { size: normalizedSize } : {}),
            includeInstallation: Boolean(otherOptions.includeInstallation ?? false),
            options: {
              ...(normalizedColor ? { color: normalizedColor } : {}),
              ...(normalizedSize ? { size: normalizedSize } : {}),
              ...otherOptions,
            },
          };

          const addResult = await apiService.cart.addItem(requestPayload);

          if (!addResult?.success) {
            const previousTotals = calculateTotals(cartItems, get().shippingType, get().deliveryConfig);
            set({
              cartItems,
              totals: previousTotals,
              isLoading: false,
              error: addResult?.error || 'Failed to add item to cart',
            });
            syncStorageForCurrentAuthState(cartItems);
            return { success: false, error: addResult?.error || 'Failed to add item to cart' };
          }

          await get().loadCart(true);
        }

        // Toast removed to prevent double notification - the component calling addToCart should handle notifications
        // showToast('Added to cart', 'success');
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || 'Failed to add item to cart';
        set({
          error: errorMessage,
          isLoading: false
        });
        return { success: false, error: errorMessage };
      }
    },

    // Remove from cart - works for both authenticated and unauthenticated users
    removeFromCart: async (productId) => {
      set({ isLoading: true, error: null });

      try {
        const { cartItems } = get();
        const targetItem = findCartItemMatch(cartItems, productId);
        const deletingKey = String(targetItem?.id || productId);

        if (!targetItem) {
          set({ isLoading: false, error: 'Item not found in cart' });
          return { success: false, error: 'Item not found in cart' };
        }

        // If authenticated, persist the deletion before reporting success.
        if (isAuthenticated()) {
          set((state) => ({
            deletingItemIds: {
              ...state.deletingItemIds,
              [deletingKey]: true,
            },
          }));

          const result = await apiService.cart.removeItem(buildCartItemRequestPayload(targetItem));

          if (!result?.success) {
            set((state) => {
              const nextDeleting = { ...state.deletingItemIds };
              delete nextDeleting[deletingKey];
              return {
                deletingItemIds: nextDeleting,
                isLoading: false,
                error: result?.error || 'Failed to remove item from cart',
              };
            });
            return { success: false, error: result?.error || 'Failed to remove item from cart' };
          }
        }

        const newItems = cartItems.filter(item => item !== targetItem);
        const newTotals = calculateTotals(newItems, get().shippingType, get().deliveryConfig);

        set((state) => {
          const nextDeleting = { ...state.deletingItemIds };
          delete nextDeleting[deletingKey];
          return {
            cartItems: newItems,
            totals: newTotals,
            deletingItemIds: nextDeleting,
            isLoading: false,
          };
        });

        syncStorageForCurrentAuthState(newItems);

        // Toast removed to prevent double notification if handled by the component
        // showToast('Removed from cart', 'info');
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || 'Failed to remove item from cart';
        const deletingKey = String(productId);
        set((state) => {
          const nextDeleting = { ...state.deletingItemIds };
          delete nextDeleting[deletingKey];
          return {
            deletingItemIds: nextDeleting,
            error: errorMessage,
            isLoading: false
          };
        });
        return { success: false, error: errorMessage };
      }
    },

    // Update item quantity - works for both authenticated and unauthenticated users
    updateQuantity: async (productId, quantity) => {
      if (quantity <= 0) {
        return await get().removeFromCart(productId);
      }

      // 1. OPTIMISTIC UPDATE: Update local state immediately
      const { cartItems } = get();
      const pid = String(productId);

      const existingItem = findCartItemMatch(cartItems, pid);

      if (!existingItem) {
        return { success: false, error: 'Item not found in cart' };
      }

      const safeQuantity = safeParseNumber(quantity, 1);
      const updatedItems = cartItems.map(item =>
        item === existingItem
          ? { ...item, quantity: safeQuantity }
          : item
      );

      const updatedTotals = calculateTotals(updatedItems, get().shippingType, get().deliveryConfig);

      set({
        cartItems: updatedItems,
        totals: updatedTotals
      });

      syncStorageForCurrentAuthState(updatedItems);

      // 2. BACKGROUND SYNC: Update server and check stock
      try {
        // If authenticated, sync to database
        if (isAuthenticated()) {
          const result = await apiService.cart.updateItem(
            {
              ...buildCartItemRequestPayload(existingItem),
              quantity: safeQuantity,
            }
          );

          if (!result?.success) {
            const previousTotals = calculateTotals(cartItems, get().shippingType, get().deliveryConfig);
            set({
              cartItems,
              totals: previousTotals,
              error: result?.error || 'Failed to update quantity',
            });
            clearLegacyCartStorage();
            return { success: false, error: result?.error || 'Failed to update quantity' };
          }
        }

        return { success: true };
      } catch (err) {
        console.error('[Cart] Quantity sync failed:', err);
        const previousTotals = calculateTotals(cartItems, get().shippingType, get().deliveryConfig);
        set({
          cartItems,
          totals: previousTotals,
          error: err.message || 'Failed to update quantity',
        });
        clearLegacyCartStorage();
        return { success: false, error: err.message || 'Failed to update quantity' };
      }
    },

    // Clear cart - works for both authenticated and unauthenticated users
    clearCart: async () => {
      set({ isLoading: true, error: null });

      try {
        const emptyTotals = { subtotal: 0, tax: 0, shipping: 0, total: 0, itemCount: 0 };

        set({
          cartItems: [],
          totals: emptyTotals,
          isLoading: false
        });

        clearLegacyCartStorage();

        // If authenticated, save to database as single object
        if (isAuthenticated()) {
          try {
            await get().saveCart();
          } catch (err) {
            console.error('[Cart] Failed to clear cart in database:', err);
          }
        }

        // Toast removed to prevent double notification
        // showToast('Cart cleared', 'info');
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || 'Failed to clear cart';
        set({
          error: errorMessage,
          isLoading: false
        });
        return { success: false, error: errorMessage };
      }
    },

    // Check if item is in cart (with type-safe comparison)
    isInCart: (productId) => {
      const { cartItems } = get();
      const pid = String(productId); // Normalize to string for consistent comparison
      return cartItems.some(
        item => String(item.productId) === pid || String(item.id) === pid || String(item.product?.id) === pid
      );
    },

    // Get item quantity
    getItemQuantity: (productId) => {
      const { cartItems } = get();
      const pid = String(productId); // Normalize to string for consistent comparison
      const item = cartItems.find(
        item => String(item.productId) === pid || String(item.id) === pid || String(item.product?.id) === pid
      );
      return item ? item.quantity : 0;
    },

    // Get cart count (number of unique products, not total quantity)
    getCartCount: () => {
      const { cartItems } = get();
      return cartItems.length;
    },

    // Get total quantity (sum of all item quantities)
    getTotalQuantity: () => {
      const { totals, cartItems } = get();
      return totals.itemCount || cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },

  };
};

export const useCartStore = create(storeCreator);

// Removed auto-save subscription - we now save explicitly in actions to avoid double-saving

// Client-side initialization - hydrate from local storage first, then sync with the API when authenticated
if (typeof window !== 'undefined') {
  const initCart = () => {
    const store = useCartStore.getState();
    if (!store.hasLoadedCart && !store.isInitializing) {
      store.loadCart();
    }
  };

  // Use requestIdleCallback for non-blocking initialization
  if (window.requestIdleCallback) {
    window.requestIdleCallback(initCart, { timeout: 1000 });
  } else {
    setTimeout(initCart, 50);
  }
}

// Export a hook that matches the old API for compatibility
export const useCart = () => {
  const cartItems = useCartStore((state) => state.cartItems);
  const totals = useCartStore((state) => state.totals);
  const deliveryConfig = useCartStore((state) => state.deliveryConfig);
  const isLoading = useCartStore((state) => state.isLoading);
  const isSyncing = useCartStore((state) => state.isSyncing);
  const deletingItemIds = useCartStore((state) => state.deletingItemIds);
  const error = useCartStore((state) => state.error);
  const addToCart = useCartStore((state) => state.addToCart);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const clearCart = useCartStore((state) => state.clearCart);
  const loadCart = useCartStore((state) => state.loadCart);
  const resetLocalCart = useCartStore((state) => state.resetLocalCart);
  const isInCart = useCartStore((state) => state.isInCart);
  const getItemQuantity = useCartStore((state) => state.getItemQuantity);
  const getCartCount = useCartStore((state) => state.getCartCount);
  const getTotalQuantity = useCartStore((state) => state.getTotalQuantity);
  const setShippingType = useCartStore((state) => state.setShippingType);

  return {
    cartItems,
    totals,
    deliveryConfig,
    isLoading,
    isSyncing,
    deletingItemIds,
    error,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    loadCart,
    resetLocalCart,
    isInCart,
    getItemQuantity,
    getCartCount,
    getTotalQuantity,
    setShippingType,
  };
};

